/**
 * OrderDetailsPage
 * Main page component for viewing detailed order information
 * Implements US-ORD-004: View Order Details
 *
 * @author SAUBHAGYA Development Team
 * @since US-ORD-004
 */

import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ArrowLeft, RefreshCw, AlertTriangle } from 'lucide-react';
import { motion } from 'framer-motion';
import { useOrderDetails } from '@/hooks/useOrderDetails';
import { OrderHeader } from '@/components/commerce/order/OrderHeader';
import { OrderLineItemsTable } from '@/components/commerce/order/OrderLineItemsTable';
import { DeliveryInfoCard } from '@/components/commerce/order/DeliveryInfoCard';
import { StatusTimeline } from '@/components/commerce/order/StatusTimeline';
import { AssociatedRecordsSection } from '@/components/commerce/order/AssociatedRecordsSection';
import { ActionButtons } from '@/components/commerce/order/ActionButtons';

/**
 * OrderDetailsPage Component
 * Comprehensive order details view with all related information
 */
export default function OrderDetailsPage() {
  const navigate = useNavigate();
  const { orderId } = useParams<{ orderId: string }>();

  const {
    order,
    loading,
    error,
    refetch,
    confirmOrder,
    cancelOrder,
    markReady,
    scheduleDelivery,
  } = useOrderDetails(orderId!);

  /**
   * Handle back navigation
   */
  function handleBack() {
    navigate('/sales/orders');
  }

  /**
   * Handle view delivery
   */
  function handleViewDelivery() {
    if (order?.deliveries && order.deliveries.length > 0) {
      navigate(`/sales/delivery/${order.deliveries[0].deliveryId}`);
    }
  }

  /**
   * Handle view invoice
   */
  function handleViewInvoice() {
    if (order?.invoices && order.invoices.length > 0) {
      navigate(`/sales/invoices/${order.invoices[0].invoiceId}`);
    }
  }

  /**
   * Loading skeleton UI
   */
  if (loading && !order) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50 py-6 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto space-y-6 max-w-7xl">
          <motion.div
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="flex items-center gap-4"
          >
            <Skeleton className="h-12 w-12 rounded-full" />
            <div className="flex-1 space-y-3">
              <Skeleton className="h-10 w-64" />
              <Skeleton className="h-4 w-96" />
            </div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <Skeleton className="h-96 w-full rounded-lg" />
              <Skeleton className="h-64 w-full rounded-lg" />
            </div>
            <div className="space-y-6">
              <Skeleton className="h-64 w-full rounded-lg" />
              <Skeleton className="h-96 w-full rounded-lg" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  /**
   * Error state
   */
  if (error && !order) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-red-50 to-slate-50 py-6 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Alert variant="destructive" className="border-2 border-red-500 bg-red-50">
              <AlertTriangle className="h-5 w-5" />
              <AlertDescription className="text-red-800 font-medium text-lg">
                {error}
              </AlertDescription>
            </Alert>
            <div className="mt-6 flex gap-3">
              <Button onClick={handleBack} variant="outline">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Orders
              </Button>
              <Button onClick={refetch}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Retry
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  /**
   * No order found
   */
  if (!order) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-amber-50 to-slate-50 py-6 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Alert className="border-2 border-amber-200 bg-amber-50">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <AlertDescription className="text-amber-800 font-medium text-lg">
                Order not found
              </AlertDescription>
            </Alert>
            <div className="mt-6">
              <Button onClick={handleBack}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Orders
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Background decorative elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-400 opacity-5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-400 opacity-5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 container mx-auto py-8 px-4 sm:px-6 lg:px-8 space-y-8 max-w-7xl">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex items-center justify-between gap-4"
        >
          <div className="flex items-center gap-4">
            <Button
              onClick={handleBack}
              variant="outline"
              size="icon"
              className="rounded-full border-slate-300 bg-white hover:bg-slate-100"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-4xl font-bold text-slate-900">Order Details</h1>
              <p className="text-sm text-slate-500 mt-1">
                View comprehensive order information and manage order status
              </p>
            </div>
          </div>
          <Button onClick={refetch} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </motion.div>

        {/* Order Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <OrderHeader order={order} />
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <ActionButtons
            orderStatus={order.orderStatus}
            onConfirm={confirmOrder}
            onCancel={cancelOrder}
            onMarkReady={markReady}
            onScheduleDelivery={scheduleDelivery}
            onViewDelivery={order.deliveries && order.deliveries.length > 0 ? handleViewDelivery : undefined}
            onViewInvoice={order.invoices && order.invoices.length > 0 ? handleViewInvoice : undefined}
          />
        </motion.div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Line Items & Associated Records */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="lg:col-span-2 space-y-6"
          >
            {/* Line Items Table */}
            <OrderLineItemsTable lineItems={order.lineItems} />

            {/* Associated Records */}
            <AssociatedRecordsSection
              invoices={order.invoices}
              deliveries={order.deliveries}
              batchAllocations={order.batchAllocations}
              payment={order.payment}
            />
          </motion.div>

          {/* Right Column - Delivery & Timeline */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="space-y-6"
          >
            {/* Delivery Information */}
            <DeliveryInfoCard delivery={order.delivery} />

            {/* Status Timeline */}
            <StatusTimeline
              timeline={order.statusTimeline}
              currentStatus={order.orderStatus}
            />
          </motion.div>
        </div>

        {/* Remarks Section */}
        {(order.remarks || order.internalNotes) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            {order.remarks && (
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h3 className="text-sm font-semibold text-blue-900 mb-2">Remarks</h3>
                <p className="text-sm text-blue-800">{order.remarks}</p>
              </div>
            )}
            {order.internalNotes && (
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <h3 className="text-sm font-semibold text-purple-900 mb-2">
                  Internal Notes
                </h3>
                <p className="text-sm text-purple-800">{order.internalNotes}</p>
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
}
